import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, STORAGE_KEYS, ERROR_MESSAGES, ROUTES } from '../constants';
import { ApiResponse, PaginatedResponse } from '../types';
import { globalLoading } from '../contexts/LoadingContext';

export class ValidationError extends Error {
  errors: Record<string, string[]>;
  constructor(message: string, errors: Record<string, string[]>) {
    super(message);
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token and start loader
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        globalLoading.increment();
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => {
        globalLoading.decrement();
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors and stop loader
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        globalLoading.decrement();
        return response;
      },
      (error: any) => {
        globalLoading.decrement();
        if (error.response?.status === 401) {
          // Only clear tokens and redirect if it's not a network error
          // This prevents clearing tokens during development when API is not available
          if (error.code !== 'NETWORK_ERROR' && error.code !== 'ECONNREFUSED') {
            console.warn('Authentication failed - clearing tokens and redirecting to login');
            localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER_DATA);
            // Use replace instead of href to avoid unload event listener warnings
            window.location.replace(ROUTES.LOGIN);
          } else {
            console.warn('Network error - API may not be available. Token preserved.');
          }
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return new Error(data.message || ERROR_MESSAGES.VALIDATION_ERROR);
        case 401:
          return new Error(ERROR_MESSAGES.UNAUTHORIZED);
        case 403:
          return new Error(ERROR_MESSAGES.FORBIDDEN);
        case 404:
          return new Error(ERROR_MESSAGES.NOT_FOUND);
        case 422:
          return new ValidationError(data.message || ERROR_MESSAGES.VALIDATION_ERROR, data.errors || {});
        case 500:
          return new Error(ERROR_MESSAGES.SERVER_ERROR);
        default:
          return new Error(data.message || ERROR_MESSAGES.SERVER_ERROR);
      }
    } else if (error.request) {
      // Network error - mark it as such
      const networkError = new Error(ERROR_MESSAGES.NETWORK_ERROR);
      (networkError as any).code = 'NETWORK_ERROR';
      return networkError;
    } else {
      // Other error
      const otherError = new Error(error.message || ERROR_MESSAGES.SERVER_ERROR);
      if (error.code === 'ECONNREFUSED') {
        (otherError as any).code = 'ECONNREFUSED';
      }
      return otherError;
    }
  }

  // Generic HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.delete(url, config);
    return response.data;
  }

  // File upload method
  async uploadFile<T = any>(
    url: string, 
    formData: FormData, 
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.api.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    });
    return response.data;
  }

  // Download file method
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.api.get(url, {
      responseType: 'blob',
    });
    
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Paginated methods
  async getPaginated<T = any>(
    url: string, 
    page: number = 1, 
    perPage: number = 15,
    config?: AxiosRequestConfig
  ): Promise<PaginatedResponse<T>> {
    const response = await this.api.get(url, {
      ...config,
      params: {
        page,
        per_page: perPage,
        ...config?.params,
      },
    });
    return this.normalizePaginatedResponse<T>(response.data);
  }

  private normalizePaginatedResponse<T>(payload: any): PaginatedResponse<T> {
    const candidate = payload?.data?.data && payload?.data?.meta
      ? payload.data
      : payload;

    return {
      data: candidate?.data || [],
      links: candidate?.links || {
        first: null,
        last: null,
        prev: null,
        next: null,
      },
      meta: candidate?.meta || {
        current_page: 1,
        from: 0,
        last_page: 1,
        links: [],
        path: '',
        per_page: 15,
        to: 0,
        total: 0,
      },
    } as PaginatedResponse<T>;
  }

}

export const apiService = new ApiService();
